import React, { useMemo } from 'react';
import { useState } from 'react';
import { initialAppData } from '../Configuration/AppData';

/**
 * Context API Setup for app data store
 *
 * @type {React.Context<{}>}
 */
export const AppContext = React.createContext({});

/**
 * Context API Setup for our application state management and helper functions.
 *
 * Helps to manage and update the global state by using a path string and more.
 *
 * @param initialAppData
 * @returns {unknown[]}
 */
function useAppData(initialAppData) {
  // Define our state and update state function
  const [appData, setAppData] = useState(initialAppData);

  // Helper function to update a key value pair in our global state by giving a path
  //     Usage: updateAppData('your.path.key', 'yourValue'');
  //     Example: <button onClick={() => updateAppData('steps.a.finished', !appData.journeys.a.finished)}>Finish Step A</button>
  const updateAppData = (objKey, objValue) => {
    const newAppData = { ...appData };

    // A helper function to update a single value
    const updateValue = (key, value) => {
      const keyParts = key.split('.');
      let currentPart = newAppData;

      for (let i = 0; i < keyParts.length; i++) {
        const part = keyParts[i];

        if (i === keyParts.length - 1) {
          currentPart[part] = value;
        } else if (!currentPart[part]) {
          currentPart[part] = {};
        }

        currentPart = currentPart[part];
      }
    };

    // If objKey is an object, iterate over its properties
    if (typeof objKey === 'object' && objKey !== null) {
      for (const key in objKey) {
        if (objKey.hasOwnProperty(key)) {
          updateValue(key, objKey[key]);
        }
      }
    } else {
      // If it's not an object, just update the single value
      updateValue(objKey, objValue);
    }

    setAppData(newAppData);
  };

  return [appData, updateAppData];
}

/**
 * Define our custom data provider specialized for global management
 *
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
export const AppStateProvider = ({ children }) => {
  // Unpack the data object and the helper function to custom constants
  const [appData, updateAppData] = useAppData(initialAppData);

  // Memorize so the state only changes if necessary
  const appDataContext = useMemo(() => ({
    appData,
    updateAppData,
  }), [appData, updateAppData]);

  // Provider our new context to the child components
  return (
    <AppContext.Provider value={appDataContext}>
      {children}
    </AppContext.Provider>
  );
}
